import React, { useEffect, useState, useRef } from "react";
import { TableRow, TableCell, IconButton } from "@mui/material";
import { Check, Close, DeleteOutline } from "@mui/icons-material";
import { AppContainer, ShowTotal } from "../../../Components/Global/Reusable";
import StyledTable from "../../../Components/Table";
import { getSuggestions, updateStatus, remove } from "../../../Services/suggestions";
import { Register } from "../Category/Register";
import { Toast } from "../../../Components/Toast";
import { setCategoryState } from "../../../features/MenuSlice";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

export default function Index() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefilledSuggestion, setPrefilledSuggestion] = useState(null);
  
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    title: "",
    variation: "",
  });

  const timerRef = useRef(0);
  const dispatch = useDispatch();
  const CategoryState = useSelector((state) => state.menu.CategoryState);

  function toggleToast(isOpen) {
    setIsToastOpen(isOpen);
  }

  function onToastOpen(title, variation) {
    setToastMessage({ title, variation });
    toggleToast(false);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(function () {
      toggleToast(true);
    }, 100);
  }

  const loadSuggestions = async () => {
    setLoading(true);
    const data = await getSuggestions();
    setSuggestions(data);
    setLoading(false);
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const handleApproveClick = (suggestion) => {
    setPrefilledSuggestion(suggestion);
    dispatch(setCategoryState()); // Open Category Registration form overlay
  };

  const handleApprovedSuggestion = (suggestionId) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === suggestionId ? { ...s, status: "approved" } : s))
    );
    setPrefilledSuggestion(null);
    onToastOpen("Sugestão Aprovada e Categoria Criada!", "success");
  };

  const handleRejectClick = async (id) => {
    try {
      await updateStatus(id, "rejected");
      setSuggestions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: "rejected" } : s))
      );
      onToastOpen("Sugestão Rejeitada", "success");
    } catch (error) {
      console.error(error);
      onToastOpen("Erro ao rejeitar sugestão", "error");
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      await remove(id);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      onToastOpen("Sugestão Removida", "success");
    } catch (error) {
      console.error(error);
      onToastOpen("Erro ao remover sugestão", "error");
    }
  };

  const getCollectionLabel = (colName) => {
    const map = {
      connectplaces: "ConnectPlaces",
      connectstores: "ConnectStores",
      connectroutes: "ConnectRoutes",
      connectguides: "ConnectGuides",
    };
    return map[colName] || colName;
  };

  return (
    <>
      <Toast
        variation={toastMessage.variation}
        title={toastMessage.title}
        open={isToastOpen}
        onOpenChange={toggleToast}
      />
      
      <AppContainer maxWidth="xl">
        <StyledTable
          TableTitle="Sugestões de Novas Categorias"
          Component={<ShowTotal total={suggestions.length} />}
          Labels={
            <TableRow>
              <TableCell>Sugestão</TableCell>
              <TableCell>Coleção / Alvo</TableCell>
              <TableCell>Data de Envio</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          }
          TableData={
            <>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">Carregando sugestões...</TableCell>
                </TableRow>
              ) : suggestions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">Nenhuma sugestão encontrada.</TableCell>
                </TableRow>
              ) : (
                suggestions.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell style={{ fontWeight: "bold" }}>{row.suggested_name}</TableCell>
                    <TableCell>{getCollectionLabel(row.collection)}</TableCell>
                    <TableCell>
                      {row.created_at
                        ? dayjs(row.created_at.toDate ? row.created_at.toDate() : row.created_at).format("DD/MM/YYYY HH:mm")
                        : "---"}
                    </TableCell>
                    <TableCell>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          backgroundColor:
                            row.status === "approved"
                              ? "#e6f4ea"
                              : row.status === "rejected"
                              ? "#fce8e6"
                              : "#fef7e0",
                          color:
                            row.status === "approved"
                              ? "#137333"
                              : row.status === "rejected"
                              ? "#c5221f"
                              : "#b06000",
                        }}
                      >
                        {row.status === "approved"
                          ? "Aprovada"
                          : row.status === "rejected"
                          ? "Rejeitada"
                          : "Pendente"}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      {row.status === "pending" && (
                        <>
                          <IconButton
                            color="success"
                            onClick={() => handleApproveClick(row)}
                            title="Aprovar e Criar Categoria"
                            size="small"
                          >
                            <Check fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="warning"
                            onClick={() => handleRejectClick(row.id)}
                            title="Rejeitar Sugestão"
                            size="small"
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(row.id)}
                        title="Deletar Sugestão"
                        size="small"
                        style={{ marginLeft: "8px" }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </>
          }
        />
        
        {CategoryState && prefilledSuggestion && (
          <Register
            editId=""
            prefilledSuggestion={prefilledSuggestion}
            onApprovedSuggestion={handleApprovedSuggestion}
            closeRegister={() => {
              dispatch(setCategoryState());
              setPrefilledSuggestion(null);
            }}
            onToastOpen={onToastOpen}
          />
        )}
      </AppContainer>
    </>
  );
}
